import React, { useEffect, useMemo } from 'react'

import { useDispatch } from 'react-redux'

import {
  ratingClearErrorAction,
  ratingErrorAction,
} from 'actions/rating/errorAction'
import { createPhotosQueueAction } from 'actions/rating/photosNextAction'
import { PromoLimit } from 'components/block/Notice/Limit/PromoLimit'
import { PromoVipLike } from 'components/block/Notice/Limit/PromoVipLike'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { locationStringUnderscore } from 'functions/location/locationStringUnderscore'
import { useAuthorized } from 'hooks/useAuthorized'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { cachePhotos } from './function/cachePhotos'
import { mergePhotos } from './function/mergePhotos'
import { useRatingQuery } from './gql/__generated__/Rating'
import { LIMIT_PHOTOS, RatingErrorLimit } from './Rating.constants'
import { RatingPhotosUpdate } from './RatingPhotosUpdate'

export const RatingNext = () => {
  const authorized = useAuthorized()
  const dispatch = useDispatch()
  const { defaultForm, coordinates, error, vip } = useShallowEqualSelector(
    ({
      photoRatingNext: { error },
      photoRatingSettings: { defaultForm },
      geolocation: { coordinates },
      userReducer: { vip },
    }) => ({
      error,
      defaultForm,
      coordinates,
      vip,
    })
  )

  const position = useMemo(() => {
    if (defaultForm.coordinates && coordinates) {
      return {
        coordinates: {
          lat: coordinates.latitude,
          lng: coordinates.longitude,
        },
      }
    } else {
      return {
        location: locationStringUnderscore([
          defaultForm.country,
          defaultForm.region,
          defaultForm.city,
          defaultForm.metro,
        ]),
      }
    }
  }, [
    coordinates,
    defaultForm.city,
    defaultForm.coordinates,
    defaultForm.country,
    defaultForm.metro,
    defaultForm.region,
  ])

  const variables = {
    ageFrom: defaultForm.ageFrom,
    ageTo: defaultForm.ageTo,
    gender: defaultForm.gender!,
    heightFrom: defaultForm.heightFrom,
    heightTo: defaultForm.heightTo,
    ...position,
    offsetPhotoId: 0,
    limit: LIMIT_PHOTOS,
    authorized,
    limitInterest: 500,
    limitCountries: 500,
    giftsLimit: 500,
  }

  const { data, loading, fetchMore, refetch } = useRatingQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables,
    onError: (...error) => {
      const gqlError = error[0].graphQLErrors[0]
      if (gqlError.extensions && gqlError.extensions.code) {
        dispatch(ratingErrorAction(gqlError.extensions.code))
      }
    },
    onCompleted: (data) => {
      dispatch(createPhotosQueueAction(data.photoRating.queue.photos))

      const _photos = data.photoRating.queue.photos
        .map((item) =>
          mergePhotos(
            item.photo,
            item.extraPhotos,
            item.photoOwner.photos.photos.nodes
          )
        )
        .map((item) => item[0])

      cachePhotos(_photos.flat())
    },
  })

  const fetchNextPhotos = () => {
    if (data?.photoRating) {
      const offsetPhotoId =
        data.photoRating.queue.photos[data.photoRating.queue.photos.length - 1]
          .photo.photoId

      fetchMore({
        variables: { offsetPhotoId },
      })
    }
  }

  useEffect(() => {
    if (vip && error === RatingErrorLimit.limit) {
      dispatch(ratingClearErrorAction())

      if (!data?.photoRating) {
        refetch()
      }
    }
  }, [data, dispatch, error, refetch, vip])

  /**
   * Первый раз
   */
  if (loading && data?.photoRating.queue.photos.length === 0) {
    return <DefaultLoader />
  }

  if (error) {
    switch (error) {
      case RatingErrorLimit.limit:
        return <PromoVipLike />
      case RatingErrorLimit.vip_limit:
        return <PromoLimit />
    }
  }

  if (data) {
    return <RatingPhotosUpdate fetchMore={fetchNextPhotos} ui={data.ui} />
  }

  return <DefaultLoader />
}
